import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {
  Container,
  Button,
  Grid,
  IconButton,
  Fade,
  Paper,
  Card,
  CardContent,
  Chip,
  Divider,
  Avatar,
  Rating
} from '@mui/material';
import CardActionArea from "@mui/material/CardActionArea";
import { styled } from "@mui/material/styles";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslatedText from '../components/TranslatedText';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { getSentence } from '../components/translations';
import CloseIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShieldIcon from '@mui/icons-material/Shield';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import StorageIcon from '@mui/icons-material/Storage';
import DownloadIcon from '@mui/icons-material/Download';
import TranslateIcon from '@mui/icons-material/Translate';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import TimerIcon from '@mui/icons-material/Timer';
import ShareIcon from '@mui/icons-material/Share';
import '../styles/landing.css';
import '../styles/slideshow.css';
import SignUpForm from '../components/auth/SignUpForm';
import SignInForm from '../components/auth/SignInForm';
import { useMultiModal } from '../hooks/useModal';
import { ServerURL } from '../components/ServerURL';
import { matomoEvent, matomoTracker } from '../components/matomoTracker';
import {Helmet} from "react-helmet";
import { MobileView, BrowserView } from "react-device-detect";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import '../css/globalVars.css';


// Animated Background Component
const AnimatedBackground = React.memo(() => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden',
      zIndex: 0,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        animation: 'float 20s ease-in-out infinite',
      },
      '@keyframes float': {
        '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
        '33%': { transform: 'translate(30px, -30px) rotate(120deg)' },
        '66%': { transform: 'translate(-20px, 20px) rotate(240deg)' }
      }
    }}
  >
    {/* Floating Geometric Shapes */}
    <Box
      sx={{
        position: 'absolute',
        top: '20%',
        left: '10%',
        width: '80px',
        height: '80px',
        background: 'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.2))',
        borderRadius: '50%',
        animation: 'bounce 6s ease-in-out infinite',
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-20px)' }
        }
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '60%',
        right: '15%',
        width: '60px',
        height: '60px',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.15))',
        transform: 'rotate(45deg)',
        animation: 'spin 8s linear infinite',
        '@keyframes spin': {
          '0%': { transform: 'rotate(45deg)' },
          '100%': { transform: 'rotate(405deg)' }
        }
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        top: '30%',
        right: '25%',
        width: '40px',
        height: '40px',
        background: 'rgba(255,255,255,0.1)',
        clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
        animation: 'pulse 4s ease-in-out infinite',
        '@keyframes pulse': {
          '0%, 100%': { opacity: 0.5, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.1)' }
        }
      }}
    />
  </Box>
));

// Enhanced Header with better animations
const Header = React.memo(({ onSignupClick, onSigninClick }) => {
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <AppBar
      position="fixed"
      sx={{
        background: 'var(--topbarPrimaryColor)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Toolbar sx={{ py: 1 }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h5"
              component="div"
              onClick={scrollToTop}
              sx={{
                fontWeight: 900,
                background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: 1.5,
                fontSize: '1.8rem',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.3s ease',
                display: 'inline-block',
                width: 'fit-content',
                '&:after': {
                  content: '"Unpen"',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  fontWeight: 900,
                  letterSpacing: 1.5,
                  fontSize: '1.8rem',
                  background: 'linear-gradient(90deg, transparent 0%, rgba(128,128,128,0.8) 50%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  backgroundPosition: '-200% 0',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  transition: 'background-position 0.6s ease',
                  pointerEvents: 'none',
                  opacity: 0
                },
                '&:hover:after': {
                  backgroundPosition: '200% 0',
                  opacity: 1
                },
                '&:hover': {
                  filter: 'brightness(1.1)'
                }
              }}
            >
              Unpen
            </Typography>
          </Box>
          <LanguageSelector />
          <Button
            color="inherit"
            onClick={onSignupClick}
            sx={{
              mx: 1,
              borderRadius: 4,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.2,
              border: '2px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.5s'
              },
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.6)',
                transform: 'translateY(-2px)',
                '&:before': {
                  left: '100%'
                }
              }
            }}
          >
            <TranslatedText textId="signup" />
          </Button>
          <Button
            color="inherit"
            onClick={onSigninClick}
            sx={{
              borderRadius: 4,
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
              py: 1.2,
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.5s'
              },
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.25)',
                transform: 'translateY(-2px)',
                '&:before': {
                  left: '100%'
                }
              }
            }}
          >
            <TranslatedText textId="signin" />
          </Button>
        </Toolbar>
      </Box>
    </AppBar>
  );
});

// Stats Section Component
const StatsSection = React.memo(() => {
  const stats = [
    { number: '1K+', label: getSentence(93), icon: <GroupIcon /> },
    { number: '98%', label: getSentence(111), icon: <CheckCircleIcon /> },
    { number: '50%', label: getSentence(94), icon: <TrendingUpIcon /> },
    { number: '24/7', label: getSentence(95), icon: <SecurityIcon /> }
  ];

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      <Grid container spacing={4}>
        {stats.map((stat, index) => (
          <Grid item size={{ xs: 6, md: 3 }} key={index}>
            <Card
              sx={{
                textAlign: 'center',
                p: 3,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.02)',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                }
              }}    
            >
              <Box sx={{ color: '#1976d2', mb: 2 }}>
                {stat.icon}
              </Box>
              <Typography
                variant="h3"
                component="div"
                sx={{
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}
              >
                {stat.number}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {stat.label}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
});

// Testimonials Section
const TestimonialsSection = React.memo(() => {
  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: getSentence(112),
      company: '', //"at companyname"
      rating: 5,
      text: getSentence(113),
      avatar: ServerURL + '/landing_page/assets/img/testimonial1.webp'
    },
    {
      name: 'Michael Chen',
      role: getSentence(114),
      company: '',
      rating: 5,
      text: getSentence(115),
      avatar: ServerURL + '/landing_page/assets/img/testimonial3.webp'
    },
    {
      name: 'Thomas Davis',
      role: getSentence(116),
      company: '',
      rating: 5,
      text: getSentence(117),
      avatar: ServerURL + '/landing_page/assets/img/testimonial2.webp'
    }
  ];

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', py: 10, position: 'relative', overflow: 'hidden' }}>
      <AnimatedBackground />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 800,
            color: 'white',
            mb: 6,
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          {getSentence(96)}
        </Typography>

        <Grid container spacing={4}>
          {testimonials.map((testimonial, index) => (
            <Grid item size={{ xs: 12, md: 4 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: 4,
                  p: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    background: 'rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={testimonial.avatar}
                    sx={{
                      width: 60,
                      height: 60,
                      mr: 2,
                      border: '3px solid rgba(25, 118, 210, 0.2)'
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0' }}>
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.role + testimonial.company}
                    </Typography>
                    <Rating value={testimonial.rating} readOnly size="small" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: 'italic',
                    lineHeight: 1.7,
                    color: 'text.secondary',
                    position: 'relative',
                    pl: 2
                  }}
                >
                  {testimonial.text}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
});

// Enhanced Features Section
const FeaturesSection = React.memo(() => {
  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 50, color: '#1976d2' }} />,
      title: getSentence(87),
      description: getSentence(88),
      color: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 50, color: '#1976d2' }} />,
      title: getSentence(89),
      description: getSentence(90),
      color: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 50, color: '#1976d2' }} />,
      title: getSentence(91),
      description: getSentence(92),
      color: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="h2"
        component="h2"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 900,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1.5,
          fontSize: { xs: '2.5rem', md: '3.5rem' }
        }}
      >
        {getSentence(85)}
      </Typography>
      <Typography
        variant="h6"
        align="center"
        color="text.secondary"
        sx={{ mb: { xs: 3, md: 4 }, maxWidth: 800, mx: 'auto', lineHeight: 1.7 }}
      >
        {getSentence(86)}
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {features.map((feature, index) => (
          <Grid item size={{ xs: 12, md: 4 }} key={index}>
            <Card
              sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: 5,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.4s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.02)',
                  boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
                  borderColor: 'rgba(25, 118, 210, 0.3)',
                  '&::before': {
                    opacity: 1
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, textAlign: 'center' }}>
                <Box
                  sx={{
                    mb: { xs: 1, md: 1.5 },
                    width: { xs: 60, md: 70 },
                    height: { xs: 60, md: 70 },
                    borderRadius: '50%',
                    background: feature.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {React.cloneElement(feature.icon, { sx: { fontSize: { xs: 30, md: 35 }, color: '#1976d2' } })}
                </Box>
                <Typography
                  variant="h4"
                  component="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    color: '#1565c0',
                    mb: { xs: 0.5, md: 1 },
                    fontSize: { xs: '1.3rem', md: '1.7rem' }
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.5,
                    fontSize: { xs: '0.95rem', md: '1rem' }
                  }}
                >
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
});

// Enhanced FAQ Section
const FAQSection = React.memo(({ faqItems }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  const handleFaqToggle = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // FAQ categories with icons
  const faqCategories = [
    { icon: <ShieldIcon />, color: '#10b981', bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }, // Security & confidentiality
    { icon: <DoNotDisturbIcon />, color: '#ef4444', bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }, // Data sharing/selling
    { icon: <TranslateIcon />, color: '#8b5cf6', bg: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)' }, // Language support
    { icon: <WifiOffIcon />, color: '#f59e0b', bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }, // Offline usage
    { icon: <TimerIcon />, color: '#06b6d4', bg: 'linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)' }, // Meeting duration
    { icon: <ShareIcon />, color: '#84cc16', bg: 'linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%)' }, // Sharing functionality
    { icon: <StorageIcon />, color: '#3b82f6', bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' }, // Data storage & compliance
    { icon: <DownloadIcon />, color: '#7d3bf6ff', bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' } // Downloading data
  ];

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: { xs: 8, md: 16 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 900,
              color: '#0f172a',
              mb: 4,
              fontSize: { xs: '2.5rem', md: '4rem' },
              lineHeight: 1.1,
              letterSpacing: '-0.02em'
            }}
          >
            {getSentence(97) + ' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {getSentence(98)}
            </Box>
          </Typography>

          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{
              mb: 2,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: '1.25rem',
              fontWeight: 400
            }}
          >
            {getSentence(99)}
          </Typography>
        </Box>

        {/* FAQ Grid */}
        <Grid container spacing={4}>
          {faqItems.map((item, index) => {
            const category = faqCategories[index % faqCategories.length];
            const isExpanded = expandedFaq === index;

            return (
              <Grid item key={index} size={{ xs: 12, md: 6 }}>
                <Card
                  onClick={() => { handleFaqToggle(index); matomoEvent('landing page action', "faq interaction", index); }}
                  sx={{
                    height: 'auto',
                    width: '100%',
                    cursor: 'pointer',
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: `2px solid ${isExpanded ? category.color : 'rgba(255, 255, 255, 0.3)'}`,
                    borderRadius: 4,
                    boxShadow: isExpanded
                      ? `0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px ${category.color}20`
                      : '0 8px 25px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    transform: isExpanded ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                    '&:hover': {
                      transform: isExpanded ? 'translateY(-8px) scale(1.02)' : 'translateY(-4px) scale(1.01)',
                      boxShadow: isExpanded
                        ? `0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px ${category.color}30`
                        : '0 15px 35px rgba(0, 0, 0, 0.12)',
                      borderColor: category.color
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}80 100%)`,
                      opacity: isExpanded ? 1 : 0,
                      transition: 'opacity 0.3s ease'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Question Header */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 3,
                      mb: isExpanded ? 3 : 0,
                      transition: 'margin-bottom 0.3s ease'
                    }}>
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          background: category.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.3s ease',
                          transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
                          boxShadow: isExpanded
                            ? `0 8px 25px ${category.color}30`
                            : '0 4px 15px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {React.cloneElement(category.icon, {
                          sx: {
                            fontSize: 28,
                            color: category.color,
                            transition: 'all 0.3s ease'
                          }
                        })}
                      </Box>

                      {/* Question Title */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: '#1e293b',
                            mb: 1,
                            lineHeight: 1.3,
                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                            pr: 2
                          }}
                        >
                          <TranslatedText textId={item.titleId} />
                        </Typography>

                        {/* Expand/Collapse Indicator */}
                        <Box sx={{
                          position: 'absolute',
                          top: 20,
                          right: 20,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: isExpanded
                            ? `linear-gradient(135deg, ${category.color} 0%, ${category.color}80 100%)`
                            : 'rgba(0, 0, 0, 0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}>
                          <ExpandMoreIcon
                            sx={{
                              fontSize: 20,
                              color: isExpanded ? 'white' : '#64748b',
                              transition: 'color 0.3s ease'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Answer Content */}
                    <Fade in={isExpanded}>
                      <Box sx={{
                        maxHeight: isExpanded ? 2000 : 0,
                        transition: 'max-height 0.4s ease'
                      }}>
                        <Divider
                          sx={{
                            mb: 3,
                            background: `${category.color}20`,
                            height: 2,
                            borderRadius: 1
                          }}
                        />
                        <Typography
                          sx={{
                            lineHeight: 1.7,
                            color: '#475569',
                            fontSize: '1rem',
                            pl: 1
                          }}
                        >
                          <TranslatedText textId={item.textId} />
                        </Typography>
                      </Box>
                    </Fade>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>


      </Container>
    </Box>
  );
});

// Enhanced Footer
const Footer = React.memo(({ language, onForgotPasswordClick }) => (
  <Box
    component="footer"
    sx={{
      background: 'var(--topbarPrimaryColor)',
      py: 8,
      mt: 0,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="1.5"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.1
      }
    }}
  >
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      <Grid container spacing={5} alignItems="center">
        <Grid item size={{ xs: 12, md: 6 }}>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 900,
              mb: 3,
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
            }}
          >
            Unpen
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 4,
              lineHeight: 1.7,
              maxWidth: 400
            }}
          >
            {getSentence(78)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={getSentence(103)}
              size="medium"
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                fontWeight: 600
              }}
            />
            <Chip
              label={getSentence(104)}
              size="medium"
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                fontWeight: 600
              }}
            />
            <Chip
              label={getSentence(105)}
              size="medium"
              sx={{
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                fontWeight: 600
              }}
            />
          </Box>
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
            <Typography variant="h5" sx={{ color: 'white', mb: 4, fontWeight: 700 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: { xs: 'center', md: 'flex-end' } }}>
              <Link
                to={`/${language === 'en' ? '' : language}/tos`}
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <TranslatedText textId="TOS" />
              </Link>
              <Link
                to={`/${language === 'en' ? '' : language}/legal`}
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  padding: '12px 24px',
                  borderRadius: '25px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <TranslatedText textId="legalMention" />
              </Link>

            </Box>
          </Box>
        </Grid>
      </Grid>
      <Divider sx={{ my: 6, background: 'rgba(255, 255, 255, 0.2)' }} />
      <Typography
        variant="body1"
        align="center"
        sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '1.1rem'
        }}
      >
        © 2025 Unpen. All rights reserved.
      </Typography>
    </Container>
  </Box>
));

const ForgotPasswordForm = React.memo(({ open, onClose, onBackToSignin }) => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    console.log('Password reset requested for', email);
    setResetEmailSent(true);
    setTimeout(() => {
      setResetEmailSent(false);
      onClose();
      setTimeout(() => onBackToSignin(), 20);
    }, 3000);
  }, [email, onClose, onBackToSignin]);

  const emailIcon = useMemo(() => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
    </svg>
  ), []);

  return (
    <Fade in={open}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 1100,
          display: open ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={onClose}
      >
        <Paper
          elevation={24}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            p: 4,
            borderRadius: 4,
            maxWidth: 450,
            width: '95%',
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'text.secondary',
              background: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                transform: 'scale(1.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h4"
            component="h2"
            align="center"
            sx={{
              mb: 3,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            <TranslatedText textId="forgotPassword" />
          </Typography>

          <Box
            sx={{
              width: '80px',
              height: '4px',
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              mx: 'auto',
              mb: 4,
              borderRadius: '2px'
            }}
          />

          {!resetEmailSent ? (
            <form onSubmit={handleSubmit}>
              <Typography
                variant="body1"
                sx={{ mb: 3, textAlign: 'center', color: 'text.secondary', lineHeight: 1.7 }}
              >
                <TranslatedText textId="resetPasswordInstructions" />
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  component="label"
                  htmlFor="reset-email"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontWeight: 600
                  }}
                >
                  <TranslatedText textId="emailLabel" />
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '2px solid',
                    borderColor: 'rgba(25, 118, 210, 0.2)',
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s',
                    '&:focus-within': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
                      background: 'rgba(255, 255, 255, 1)'
                    }
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      mr: 1.5,
                      color: 'primary.main',
                      display: 'flex'
                    }}
                  >
                    {emailIcon}
                  </Box>
                  <input
                    type="email"
                    id="reset-email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '4px',
                      border: 'none',
                      outline: 'none',
                      fontSize: '16px',
                      backgroundColor: 'transparent',
                      fontFamily: 'inherit'
                    }}
                    placeholder={getSentence("email", language)}
                  />
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  mt: 2,
                  borderRadius: 3,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(25, 118, 210, 0.4)'
                  }
                }}
              >
                <TranslatedText textId="sendResetLink" />
              </Button>
            </form>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box
                sx={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" fill="white" viewBox="0 0 16 16">
                  <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                </svg>
              </Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                <TranslatedText textId="resetLinkSent" />
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}>
                <TranslatedText textId="checkEmailInstructions" />
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <TranslatedText textId="backToLogin" /> {' '}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: 'primary.dark'
                  }
                }}
                onClick={onBackToSignin}
              >
                <TranslatedText textId="signin" />
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
});

// Enhanced Floating CTA with better animations
const FloatingCTA = React.memo(({ show, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Fade in={show}>
      <Box
        sx={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000
        }}
      >
        {/* Main CTA Button */}
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: 'relative',
            animation: show ? 'float 3s ease-in-out infinite' : 'none',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-8px)' }
            }
          }}
        >
          {/* Glowing Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              left: -4,
              right: -4,
              bottom: -4,
              borderRadius: '35px',
              background: 'var(--topbarPrimaryColor)',
              opacity: isHovered ? 0.6 : 0.3,
              filter: 'blur(8px)',
              transition: 'opacity 0.3s ease',
              zIndex: -1
            }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={onClick}
            startIcon={<PlayArrowIcon />}
            sx={{
              zIndex: 9999,
              py: 2,
              px: 5,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: '30px',
              background: 'var(--topbarPrimaryColor)',
              boxShadow: '0 12px 40px rgba(var(--topbarsecondaryColor), 0.4)',
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s ease'
              },
              '&:hover': {
                transform: 'translateY(-8px) scale(1.05)',
                boxShadow: '0 20px 60px rgba(var(--topbarsecondaryColor), 0.6)',
                background: 'var(--topbarsecondaryColor)',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                '&:before': {
                  left: '100%'
                }
              },
              '&:active': {
                transform: 'translateY(-4px) scale(1.02)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                <TranslatedText textId="calltoaction1" />
              </Typography>
            </Box>
          </Button>

          {/* Pulse Ring Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              borderRadius: '30px',
              border: '3px solid var(--topbarsecondaryColor)',
              animation: 'pulse-ring 3s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(1)',
                  opacity: 0.8,
                  borderWidth: '3px'
                },
                '50%': {
                  transform: 'translate(-50%, -50%) scale(1.15)',
                  opacity: 0.4,
                  borderWidth: '2px'
                },
                '100%': {
                  transform: 'translate(-50%, -50%) scale(1.3)',
                  opacity: 0,
                  borderWidth: '1px'
                }
              }
            }}
          />
          {/* Secondary Ring Effect */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              borderRadius: '30px',
              border: '2px solid rgba(var(--topbarsecondaryColor), 0.4)',
              animation: 'pulse-ring-secondary 3s ease-out infinite 1.5s',
              '@keyframes pulse-ring-secondary': {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(1)',
                  opacity: 0.6,
                  borderWidth: '2px'
                },
                '50%': {
                  transform: 'translate(-50%, -50%) scale(1.1)',
                  opacity: 0.3,
                  borderWidth: '1px'
                },
                '100%': {
                  transform: 'translate(-50%, -50%) scale(1.25)',
                  opacity: 0,
                  borderWidth: '0px'
                }
              }
            }}
          />
        </Box>
      </Box>
    </Fade>
  );
});

// Premium Carousel Component
const PremiumCarousel = React.memo(({ slides, openImageModal }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [slides.length, isAutoPlaying]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    matomoEvent('landing page action', "next slide", "button");
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    matomoEvent('landing page action', "previous slide", "button");
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    matomoEvent('landing page action', "go to slide " + index, "button");
  });

  return (
    <Box sx={{
      py: { xs: 8, md: 16 },
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #fafbfc 0%, #f8fafc 100%)'
    }}>
      {/* Background Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)
        `,
        zIndex: 0
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Carousel */}
        <Box sx={{ position: 'relative', maxWidth: 1400, mx: 'auto' }}>
          {/* Slides Container */}
          <Box sx={{
            position: 'relative',
            height: { xs: 900, md: 700 },
            borderRadius: 6,
            overflow: 'hidden'
          }}>
            {slides.map((slide, index) => {
              const isActive = index === currentSlide;
              const isPrev = index === (currentSlide - 1 + slides.length) % slides.length;
              const isNext = index === (currentSlide + 1) % slides.length;

              return (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'scale(1) translateX(0)' :
                      isPrev ? 'scale(0.95) translateX(-50px)' :
                        isNext ? 'scale(0.95) translateX(50px)' : 'scale(0.9)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: isActive ? 2 : 1,
                    filter: isActive ? 'none' : 'blur(1px)',
                    pointerEvents: isActive ? 'auto' : 'none'
                  }}
                >
                  <Grid container spacing={0} sx={{ height: '100%' }}>
                    {/* Content Side */}
                    <Grid item size={{ xs: 12, md: 5 }} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: { xs: 4, md: 8 },
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(139, 92, 246, 0.02) 100%)',
                        zIndex: 0
                      }
                    }}>
                      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: { xs: '2rem', md: '3rem' },
                            fontWeight: 800,
                            color: '#0f172a',
                            mb: 4,
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                            opacity: 0,
                            animation: isActive ? 'slideInFromLeft 0.8s ease-out 0.2s forwards' : 'none',
                            '@keyframes slideInFromLeft': {
                              'from': { opacity: 0, transform: 'translateX(-50px)' },
                              'to': { opacity: 1, transform: 'translateX(0)' }
                            }
                          }}
                        >
                          <TranslatedText textId={slide.titleId} dangerousHTML />
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            color: '#64748b',
                            fontSize: '1.25rem',
                            lineHeight: 1.6,
                            mb: 6,
                            fontWeight: 400,
                            opacity: 0,
                            animation: isActive ? 'slideInFromLeft 0.8s ease-out 0.4s forwards' : 'none'
                          }}
                        >
                          <TranslatedText textId={slide.contentId} dangerousHTML />
                        </Typography>

                        {/* Features List */}
                        <Box sx={{ mb: 6 }}>
                          {(() => {
                            const slideFeatures = [
                              [getSentence(82), getSentence(83), getSentence(84)], // Slide 1: Meeting transcription
                              [getSentence(106), getSentence(108), getSentence(119)], // Slide 2: Main platform features
                              [getSentence(82)], // Slide 3: Recording features
                              [getSentence(107), getSentence(84), getSentence(109)], // Slide 4: Transcription capabilities
                              [getSentence(104), getSentence(110)] // Slide 5: Privacy features
                            ];
                            return slideFeatures[index]?.map((feature, idx) => (
                              <Box key={idx} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                                opacity: 0,
                                animation: isActive ? `slideInFromLeft 0.6s ease-out ${0.6 + idx * 0.15}s forwards` : 'none'
                              }}>
                                <Box sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} />
                                </Box>
                                <Typography sx={{ color: '#374151', fontWeight: 500 }}>
                                  {feature}
                                </Typography>
                              </Box>
                            ));
                          })()}
                        </Box>


                      </Box>
                    </Grid>

                    {/* Image Side */}
                    <Grid item size={{ xs: 12, md: 7 }} sx={{ position: 'relative', overflow: 'hidden' }}>
                      <Box
                        onClick={() => openImageModal(slide.image)}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)',
                            zIndex: 1
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            maxWidth: '90%',
                            maxHeight: '80%',
                            borderRadius: 4,
                            overflow: 'clip',
                            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.15)',
                            transform: isActive ? 'scale(1) rotate(0deg)' : 'scale(0.95) rotate(-1deg)',
                            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1px solid rgba(255, 255, 255, 0)',
                            background: 'rgba(255,255,255,0)',
                            opacity: 0,
                            animation: isActive ? 'slideInFromRight 0.8s ease-out 0.3s forwards' : 'none',
                            '@keyframes slideInFromRight': {
                              'from': { opacity: 0, transform: 'translateX(50px) scale(0.95)' },
                              'to': { opacity: 1, transform: 'translateX(0) scale(1)' }
                            }
                          }}
                        >
                          <Box
                            component="img"
                            src={slide.image}
                            alt={`Feature ${index + 1}`}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              transition: 'all 0.5s ease'
                            }}
                          />

                          {/* Hover Overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                              opacity: 0,
                              transition: 'opacity 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover': { opacity: 1 }
                            }}
                          >
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)'
                                }
                              }}
                            >
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                <path d="M15 12L12 9V15L15 12ZM21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z" fill="#6366f1" />
                              </svg>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {/* Left Navigation Button */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: { xs: -20, md: -60 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: { xs: 50, md: 60 },
              height: { xs: 50, md: 60 },
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>

          {/* Right Navigation Button */}
          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: { xs: -20, md: -60 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: { xs: 50, md: 60 },
              height: { xs: 50, md: 60 },
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </IconButton>

          {/* Pill Indicators Overlay */}
          <Box sx={{
            position: 'absolute',
            
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1.5,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 50,
            p: 2,
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
            zIndex: 10
          }}>
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                sx={{
                  width: currentSlide === index ? 32 : 12,
                  height: 12,
                  borderRadius: 6,
                  background: currentSlide === index
                    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                    : '#cbd5e1',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: currentSlide === index
                      ? 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)'
                      : '#94a3b8',
                    transform: 'scale(1.1)'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

matomoTracker(true)

const CardContentContent = styled(CardContent)({
  padding: "2rem",
  textAlign: "center",
  backgroundColor: "transparent",
  borderRadius: "0 0 16px 16px"
});

const TypographyTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: "2.5rem",
  color: "#0f172a",
  textAlign: 'center',
  textTransform: "uppercase",
  margin: "1rem 0",
  fontWeight: 800,
  letterSpacing: '-0.02em'
});

const TypographySubtitle = styled(Typography)({
  fontFamily: "Montserrat",
  color: "#64748b",
  marginTop: "1rem",
  textAlign: 'center',
  fontWeight: 500,
  fontSize: "1.5rem",
  marginBottom: "1.5rem",
  lineHeight: 1.6
});

const StyledCard = styled(Card)(({ theme }) => ({
  minWidth: 256,
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 16,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px) scale(1.02)',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.15)',
    borderColor: 'rgba(25, 118, 210, 0.3)',
    '&::before': {
      opacity: 1
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  }
}));


const CustomCard = ({ title, subtitle }) => {
  return (
    <StyledCard>
      <CardActionArea>
        <CardContentContent>
          <TypographyTitle variant="h2">{title}</TypographyTitle>
          <div style={{ 
            borderBottom: "2px solid rgba(0, 0, 0, 0.1)", 
            margin: "1.5rem 0",
            width: "80%",
            marginLeft: "auto",
            marginRight: "auto"
          }} />
          <TypographySubtitle>{subtitle}</TypographySubtitle>
        </CardContentContent>
      </CardActionArea>
    </StyledCard>
  );
};

function PriceCards(toptext, title1, subtitle1, title2, subtitle2, title3, subtitle3) {

  let grid = ""

  if (title1 !== "")
  {
    grid = <Grid container spacing={4} justifyContent="center" sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
              <Grid item xs={12} md={4}>
                  <CustomCard 
                    color="#bfdbfc" 
                    title={title1}
                    subtitle={subtitle1}
                  />
              </Grid>

              <Grid item xs={12} md={4}>
                  <CustomCard 
                    color="#bfdbfc" 
                    title={title2}
                    subtitle={subtitle2}
                  />
              </Grid>

              <Grid item xs={12} md={4}>
                  <CustomCard 
                    color="#bfdbfc" 
                    title={title3}
                    subtitle={subtitle3}
                  />
              </Grid>
            </Grid>
  }

  return (
    <Box sx={{ 
      width: '100%', 
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0
      }
    }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 8 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            textAlign: "center", 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
            backdropFilter: 'blur(20px)',
            padding: '3rem',
            marginBottom: '4rem',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: "Montserrat", 
              color: '#64748b',
              maxWidth: '800px',
              margin: '0 auto',
              lineHeight: 1.7,
              fontSize: '1.25rem',
              fontWeight: 400
            }}
          >
            {toptext}
          </Typography>
        </Paper>
        
        {grid}
      </Container>
    </Box>
  );
}


function PriceTabs() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

    const handleChange2 = (event) => {
    setValue(event.target.value);
  };

  return (
    <Box sx={{background: "linear-gradient(180deg, #fafbfc 0%, #f8fafc 100%)"}}>
      <Box sx={{background: `radial-gradient(circle at 25% 25%,rgba(99, 102, 241, 0.05) 0%, transparent 50%), 
        radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)`,
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 4, md: 8 }}}
      >

        <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 900,
                color: '#0f172a',
                mb: 4,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                textAlign: "center",
              }}
            >
              {""}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {"Nos tarifs"}
              </Box>
            </Typography>

        <Box>
          <MobileView>
            <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={value}
                      label="Age"
                      onChange={handleChange2}
                      sx={{borderRadius: "20px", 
                                        backgroundColor: value === 2 ? "rgba(82, 19, 147, 0.6)" : "rgba(82, 19, 147, 0.3)",
                                        color: "rgba(0,0,0,0.8)",
                                        '&.Mui-selected': {
                                          color: '#fff',
                                        },
                                        textAlign: "center",
                                        fontWeight: "bold",
                                      display: 'flex',       // Set display to flex to center it
                          justifyContent: 'center', // Center horizontally
                          alignItems: 'center', // Center vertically
                          width: '100%', // Ensure it uses full width of the parent container
                          margin: '0 auto',
                        outline: 'none'}}
                    >
            <MenuItem value={0}>{getSentence(188)}</MenuItem>
            <MenuItem value={2}>{getSentence(189)}</MenuItem>
            <MenuItem value={4}>{getSentence(190)}</MenuItem>
            <MenuItem value={6}>Entreprise</MenuItem>
          </Select>
          </MobileView>
          <BrowserView>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" centered indicatorColor="rgba(0,0,0,0)" >
              <Tab label={getSentence(188)} sx={{borderRadius: "20px", 
                            backgroundColor: value === 0 ? "rgba(82, 19, 147, 0.6)" : "rgba(82, 19, 147, 0.3)",
                            color: "rgba(0,0,0,0.8)",
                            '&.Mui-selected': {
                              color: '#fff',
                            },
                            '@media (max-width: 600px)': {
                minWidth: '80px', // Smaller width for mobile
                padding: '6px 12px', // Smaller padding on mobile
              },
                            fontWeight: "bold"}}/>
              <Tab disabled label="" sx={{borderRadius: "20px", backgroundColor: "rgba(56, 16, 16, 0)", minWidth: "5px"}}/>
              <Tab label={getSentence(189)} sx={{borderRadius: "20px", 
                            backgroundColor: value === 2 ? "rgba(82, 19, 147, 0.6)" : "rgba(82, 19, 147, 0.3)",
                            color: "rgba(0,0,0,0.8)",
                            '&.Mui-selected': {
                              color: '#fff',
                            },
                            fontWeight: "bold"}}/>
              <Tab disabled label="" sx={{borderRadius: "20px", backgroundColor: "rgba(0, 0, 0, 0)", minWidth: "5px"}}/>
              <Tab label={getSentence(190)} sx={{borderRadius: "20px", 
                            backgroundColor: value === 4 ? "rgba(82, 19, 147, 0.6)" : "rgba(82, 19, 147, 0.3)",
                            color: "rgba(0,0,0,0.8)",
                            '&.Mui-selected': {
                              color: '#fff',
                            },
                            fontWeight: "bold"}}/>
              <Tab disabled label="" sx={{borderRadius: "20px", backgroundColor: "rgba(0, 0, 0, 0)", minWidth: "5px"}}/>
              <Tab label="Entreprise" sx={{borderRadius: "20px", 
                            backgroundColor: value === 6 ? "rgba(82, 19, 147, 0.6)" : "rgba(82, 19, 147, 0.3)",
                            color: "rgba(0,0,0,0.8)",
                            '&.Mui-selected': {
                              color: '#fff',
                            },
                            fontWeight: "bold"
              }}/>
            </Tabs>
          </BrowserView>
        </Box>
        <Box value={value} index={0}  hidden={value !== 0}>
          {PriceCards(getSentence(191),"15€", "10h / "+getSentence(196), "55€", "50h / "+getSentence(196), "120€", "120h / "+getSentence(196))}
        </Box>
        <Box value={value} index={1}  hidden={value !== 2}>
          {//Pay for the whole year at once and get a % reduction.
          }
          {PriceCards(getSentence(192), "160€", "10h / "+getSentence(196), "600€", "50h / "+getSentence(196), "1300€", "120h / "+getSentence(196))}
        </Box>
        <Box value={value} index={2}  hidden={value !== 4}>
          {PriceCards(getSentence(193), "2€", "1h", "15€", "10h", "60€", "50h")}
        </Box>
        <Box value={value} index={3}  hidden={value !== 6}>
          {PriceCards(getSentence(194), "")}
        </Box>
      </Box>
    </Box>
  );
}

const LandingPage = () => {
  const { language } = useLanguage();
  const { modals, openSignup, openSignin, openForgotPassword, closeSignup, closeSignin, closeForgotPassword, switchModal } = useMultiModal();

  const [imgModal, setImgModal] = useState(false);
  const [currentImgSrc, setCurrentImgSrc] = useState('');
  const [showFloatingCta, setShowFloatingCta] = useState(false);
  const [videoModal, setVideoModal] = useState(false);

  // Memoized slide data
  const slides = useMemo(() => [
    {
      titleId: 'title1',
      contentId: 'p1',
      image: ServerURL + '/landing_page/assets/img/meeting.jpg'
    },
    {
      titleId: 'title1bis',
      contentId: 'p1bis',
      image: ServerURL + '/landing_page/assets/img/main_page.avif'
    },
    {
      titleId: 'title2',
      contentId: 'p2',
      image: ServerURL + '/landing_page/assets/img/recording2.avif'
    },
    {
      titleId: 'title3',
      contentId: 'p3',
      image: ServerURL + '/landing_page/assets/img/transciption.avif'
    },
    {
      titleId: 'title4',
      contentId: 'p4',
      image: ServerURL + '/landing_page/assets/img/no_use_of.avif'
    }
  ], []);

  // Memoized FAQ items
  const faqItems = useMemo(() => [
    { titleId: 'FAQ_title1', textId: 'FAQ_text1' },
    { titleId: 'FAQ_title2', textId: 'FAQ_text2' },
    { titleId: 'FAQ_title4', textId: 'FAQ_text4' },
    { titleId: 'FAQ_title5', textId: 'FAQ_text5' },
    { titleId: 'FAQ_title6', textId: 'FAQ_text6' },
    { titleId: 'FAQ_title7', textId: 'FAQ_text7' },
    { titleId: 'FAQ_title3', textId: 'FAQ_text3' }
  //  { titleId: 'FAQ_title8', textId: 'FAQ_text8' }
  ], []);

  // Initialize effects
  useEffect(() => {
    document.getElementById('navigatorLanguage')?.setAttribute('value', navigator.language);

    const handleScroll = () => {
      setShowFloatingCta(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Memoized handlers
  const openImageModal = useCallback((imgSrc) => {
    setCurrentImgSrc(imgSrc);
    setImgModal(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setImgModal(false);
  }, []);

  const openVideoModal = useCallback(() => {
    setVideoModal(true);
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModal(false);
  }, []);

  const handleBackdropClick = useCallback(() => {
    if (modals.signup) closeSignup();
    else if (modals.signin) closeSignin();
    else if (modals.forgotPassword) closeForgotPassword();
  }, [modals, closeSignup, closeSignin, closeForgotPassword]);

  const switchToSignin = useCallback(() => {
    switchModal('signup', 'signin');
  }, [switchModal]);

  const switchToSignup = useCallback(() => {
    switchModal('signin', 'signup');
  }, [switchModal]);

  const switchToForgotPassword = useCallback(() => {
    switchModal('signin', 'forgotPassword');
  }, [switchModal]);

  const backToSignin = useCallback(() => {
    closeForgotPassword();
    setTimeout(() => openSignin(), 20);
  }, [closeForgotPassword, openSignin]);


  // Enhanced CTA Section
  const CTASection = React.memo(({ onSignupClick }) => (
    <Box
      sx={{
        background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0-20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z"/%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.1
        }
      }}
    >
      <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h2"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 800,
            color: 'white',
            mb: 3,
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}
        >
          {getSentence(100)}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            mb: 6,
            lineHeight: 1.7
          }}
        >
          {getSentence(101)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={onSignupClick}
            startIcon={<PlayArrowIcon />}
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 5,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
              color: '#1976d2',
              boxShadow: '0 8px 30px rgba(255, 255, 255, 0.3)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 12px 40px rgba(255, 255, 255, 0.4)',
                background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)'
              }
            }}
          >
            {getSentence(80)}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              matomoEvent('landing page action', "open demo video", "call to action 2");
              //open video modal here
              openVideoModal();
            }}
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.2rem',
              fontWeight: 600,
              borderRadius: 5,
              textTransform: 'none',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-3px)'
              }
            }}
          >
            {getSentence(81)}
          </Button>
        </Box>
      </Container>
    </Box>
  ));

  return (
    <div className="landing-page">
      <Helmet>{/*add meta tags */}
        <meta name="keywords" content={getSentence(120)}/>
        <meta name="description" content={getSentence(121)}></meta>
      </Helmet>
      <Header onSignupClick={() => {
        matomoEvent('landing page action', "open signup", "top button");
        openSignup();
      }}
        onSigninClick={() => {
          matomoEvent('landing page action', "open signin", "top button");
          openSignin()
        }} />

      {/* Enhanced Hero Section */}
      <Box
        sx={{
          pt: 10,
          pb: 8,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <AnimatedBackground />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item size={{ xs: 12, md: 7 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  sx={{
                    color: 'white',
                    fontWeight: 900,
                    textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    mb: 4,
                    fontSize: { xs: '3rem', md: '4.5rem' },
                    lineHeight: 1.1
                  }}
                >
                  {getSentence(102) + ' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Unpen
                  </Box>
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    mb: 6,
                    lineHeight: 1.6,
                    fontWeight: 400,
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  {getSentence(79)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => { matomoEvent('landing page action', "open signup", "call to action 1"); openSignup() }}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      py: 2.5,
                      px: 8,
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      borderRadius: 6,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                      color: '#1976d2',
                      boxShadow: '0 8px 30px rgba(255, 255, 255, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 40px rgba(255, 255, 255, 0.4)',
                        background: 'linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%)'
                      }
                    }}
                  >
                    {getSentence(80)}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => { matomoEvent('landing page action', "open demo video", "call to action 1"); openVideoModal() }}
                    sx={{
                      py: 2.5,
                      px: 6,
                      fontSize: '1.3rem',
                      fontWeight: 600,
                      borderRadius: 6,
                      textTransform: 'none',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      color: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      borderWidth: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                        background: 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-3px)',
                        borderWidth: 2
                      }
                    }}
                  >
                    {getSentence(81)}
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  textAlign: 'center',
                  display: { xs: 'none', md: 'block' }
                }}
              >
                <Box
                  onClick={() => { matomoEvent('landing page action', "open demo video", "video "); openVideoModal() }}
                  sx={{
                    width: '100%',
                    maxWidth: 400,
                    height: 300,
                    mx: 'auto',
                    borderRadius: 4,
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundImage: 'url(https://img.youtube.com/vi/dmsrU6s8Oug/maxresdefault.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
                      borderColor: 'rgba(255, 255, 255, 0.6)'
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 100%)',
                      zIndex: 1
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
                      animation: 'shimmer 3s ease-in-out infinite',
                      zIndex: 2,
                      '@keyframes shimmer': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(100%)' }
                      }
                    }
                  }}
                >
                  {/* Play Button Overlay */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      zIndex: 3,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
                      border: '3px solid rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 1)',
                        transform: 'scale(1.1)',
                        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.4)'
                      }
                    }}
                  >
                    <PlayArrowIcon sx={{
                      fontSize: 40,
                      color: '#1976d2',
                      ml: 1,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                    }} />
                  </Box>

                  {/* Video Info Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                      p: 3,
                      zIndex: 3
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
                        mb: 0.5
                      }}
                    >
                      {getSentence(186)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)'
                      }}
                    >
                      {getSentence(136)}
                    </Typography>
                  </Box>

                  {/* Duration Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 15,
                      right: 15,
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      zIndex: 3,
                      backdropFilter: 'blur(5px)'
                    }}
                  >
                    {getSentence(187)}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Premium Carousel */}
      <PremiumCarousel
        slides={slides}
        openImageModal={openImageModal}
      />

      {/* Features Section */}
      <FeaturesSection />

      {/* Stats Section }
      <StatsSection />

      {/* Testimonials Section */}


      {PriceTabs()}
      
      {/*<TestimonialsSection />*/}

      {/* FAQ Section */}
      <FAQSection faqItems={faqItems} />

      {/* Enhanced CTA Section */}
      <CTASection onSignupClick={() => { matomoEvent('landing page action', "open signup", "call to action 2"); openSignup() }} />

      {/* Floating CTA */}
      <FloatingCTA show={showFloatingCta} onClick={() => { matomoEvent('landing page action', "open signup", "floating call to action"); openSignup() }} />

      {/* Enhanced Footer */}
      <Footer language={language} onForgotPasswordClick={openForgotPassword} />

      {/* Modal Background */}
      {modals.background && (
        <Box
          onClick={handleBackdropClick}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(5px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      )}

      {/* Auth Forms */}
      <SignUpForm
        open={modals.signup}
        onClose={closeSignup}
        onSwitchToSignin={switchToSignin}
      />

      <SignInForm
        open={modals.signin}
        onClose={closeSignin}
        onSwitchToSignup={switchToSignup}
        onSwitchToForgotPassword={switchToForgotPassword}
      />

      <ForgotPasswordForm
        open={modals.forgotPassword}
        onClose={closeForgotPassword}
        onBackToSignin={backToSignin}
      />

      {/* Video Demo Modal */}
      {videoModal && (
        <Fade in={videoModal}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1500,
              p: 2
            }}
            onClick={closeVideoModal}
          >
            <IconButton
              onClick={closeVideoModal}
              sx={{
                position: 'absolute',
                top: 30,
                right: 30,
                color: 'white',
                fontSize: '2rem',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: '100%',
                maxWidth: 800,
                aspectRatio: '16/9',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                background: '#000'
              }}
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dmsrU6s8Oug" 
                title="Unpen Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerpolicy="strict-origin-when-cross-origin" 
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
              />
            </Box>
          </Box>
        </Fade>
      )}

      {/* Enhanced Image Modal */}
      {imgModal && (
        <Fade in={imgModal}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1500,
            }}
            onClick={closeImageModal}
          >
            <IconButton
              onClick={closeImageModal}
              sx={{
                position: 'absolute',
                top: 30,
                right: 30,
                color: 'white',
                fontSize: '2rem',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={currentImgSrc}
              alt="Enlarged view"
              sx={{
                maxHeight: '90%',
                maxWidth: '90%',
                borderRadius: 2,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
              }}
            />
          </Box>
        </Fade>
      )}
    </div>
  );
};

export default LandingPage; 